import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import Highcharts, { Chart, LegendOptions, Options, PointClickEventObject, SeriesBarOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { cloneDeep } from 'lodash-es';

export const STACKED_BAR_CHART_DEFAULT_OPTIONS: Options = {
    title: {
        text: undefined,
    },
    chart: {
        type: 'bar',
        borderWidth: 0,
    },
    xAxis: {
        type: 'category',
        categories: [],
        lineColor: 'transparent',
        lineWidth: 0,
        gridLineColor: 'transparent',
        gridLineWidth: 0,
        labels: {
            enabled: false,
        },
    },
    yAxis: {
        title: {
            text: undefined,
        },
        labels: {
            enabled: false,
        },
        lineColor: 'transparent',
        gridLineColor: 'transparent',
        stackLabels: {
            enabled: false,
        },
        type: 'category',
    },
    plotOptions: {
        bar: {
            stacking: 'percent',
            pointWidth: 50,
            borderRadius: {
                radius: 8,
                scope: 'stack',
                where: 'all',
            },
            dataLabels: {
                enabled: false,
            },
        },
    },
    credits: {
        enabled: false,
    },
    series: undefined,
    tooltip: {
        format: '<span>{series.name}: <b>{point.y}</b></span>',
    },
    legend: {
        enabled: true,
        align: 'left',
    },
};

function createDefaultLegendOptions(totalCount: number): LegendOptions {
    return {
        align: 'left',
        labelFormatter: function () {
            const count =
                'userOptions' in this
                    ? ((this.userOptions as SeriesBarOptions).data?.[0] as number | undefined)
                    : undefined;
            const percent = totalCount > 0 ? ((count ?? 0) / totalCount) * 100 : 0;
            return this.name + ' <b>(' + percent.toFixed(0) + '%)</b>';
        },
        reversed: true,
    };
}

@Component({
    selector: 'vc-stacked-bar-chart',
    imports: [HighchartsChartComponent],
    templateUrl: './stacked-bar-chart.component.html',
    styleUrl: './stacked-bar-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StackedBarChartComponent {
    highcharts: typeof Highcharts = Highcharts;

    options: Options = cloneDeep(STACKED_BAR_CHART_DEFAULT_OPTIONS);

    private chartInstance?: Chart;

    @Input()
    height = '148px';

    @Input()
    set spacing(value: number | undefined) {
        if (value === undefined) {
            return;
        }

        this.options = {
            ...this.options,
            chart: {
                ...this.options.chart,
                spacingLeft: value,
                spacingRight: value,
            },
        };
        this._updateChart();
    }

    @Input()
    set series(value: SeriesBarOptions[]) {
        this.options = {
            ...this.options,
            series: value,
        };
        this._updateChart();
    }

    @Input()
    set totalCount(value: number) {
        this.options = {
            ...this.options,
            legend: createDefaultLegendOptions(value),
        };
        this._updateChart();
    }

    @Input()
    set tooltipFormat(value: string | undefined) {
        if (!value) {
            return;
        }
        this.options = {
            ...this.options,
            tooltip: {
                ...this.options.tooltip,
                format: value,
            },
        };
        this._updateChart();
    }

    @Input()
    set pointClick(value: ((event: PointClickEventObject) => void) | undefined) {
        this.options = {
            ...this.options,
            plotOptions: {
                ...this.options.plotOptions,
                series: {
                    ...this.options.plotOptions?.series,
                    cursor: value ? 'pointer' : 'default',
                    point: {
                        ...this.options.plotOptions?.series?.point,
                        events: {
                            ...this.options.plotOptions?.series?.point?.events,
                            click: value,
                        },
                    },
                },
            },
        };
        this._updateChart();
    }

    onChartInstance(chart: Chart): void {
        this.chartInstance = chart;
        this._updateChart();
    }

    private _updateChart(): void {
        if (!this.chartInstance?.renderer) {
            return;
        }

        this.chartInstance.update(this.options, true, true);
    }
}
